import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { Button, Text, useToast, Box, VStack, HStack, IconButton } from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiUpload } from 'react-icons/fi';
import { useDropzone } from 'react-dropzone';
import { CollabDataContext } from 'contexts/collabDataContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { deliverableApi } from '../../../services/api/deliverable';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getChannelDisplayName } from '../../../utils/channelUtils';
import { getInfluencerPrimaryHandle } from '../../../utils/influencerUtils';
import {  ContentSubmissionUnit, SocialChannel, isVoided } from '@foodfluence/shared';
import { storage } from '../../../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { ugcSubmissionPath } from '../utils/collabRoutes';
import { getCollabContentRequirements, getContentTypeDisplayName, isCarouselRequirement, getContentTypeCount, hasNewContent } from '../../../utils/contentUtils';
import { getGroupedContent, isGroupApproved, areAllSlotsCovered } from '../../../utils/groupedContentUtils';
import { 
  getAcceptedFileTypes, 
  triggerFileSelection, 
  formatFileSize, 
  isImageFile, 
  isVideoFile, 
  getFileTypeDisplayText, 
  uploadWithProgress, 
  checkContentOverflow, 
  scrollContentContainer 
} from '../../../utils/contentUploadUtils';
import { ContentPreview } from '../components/ContentPreview';
import { ContentLinkSubmission } from '../components/ContentLinkSubmission';
import { ContentRequirements } from '../components/ContentRequirements';
import { ContentUploadSection } from '../components/ContentUploadSection';
import UploadProgress, { UploadProgressRef } from '../components/UploadProgress';
import ViewGuidelinesSheet from '../components/creative-brief/ViewGuidelinesSheet';
import { SubmissionHeader, SUBMISSION_MAX_W } from '../components/SubmissionPageParts';
import { ff } from '../../../theme';

interface ChannelLink {
  channel: SocialChannel;
  link: string;
  status?: 'approved' | 'pendingVerification' | 'pendingSubmission';
  submittedLink?: string;
  rejectionReason?: string;
  /** The brand withdrew this channel's slot — shown as removed instead of asking for a link. */
  voided?: boolean;
}

const ContentUploadPage: React.FC = () => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const collabId = queryParams.get('id');
  const contentId = queryParams.get('contentId');
  const contentKey = queryParams.get('contentKey');
  const viewOnly = queryParams.get('viewOnly') === 'true';

  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const { influencerData } = useContext(InfluencerDataContext);
  const collab = collabData.find(c => c.id === collabId);
  const campaign = campaignData[collabId!];
  const clients = useContext(ClientDataContext);
  const client = clients[campaign?.clientID];
  const clientName = client?.accountName || '';
  
  // Get content requirement and all related content
  const contentRequirementsMap = useMemo(
    () => getCollabContentRequirements(collab, campaign),
    [collab, campaign]
  );
  const contentRequirement = contentId ? contentRequirementsMap?.[contentId] : undefined;
  const contentMap = (collab?.content || {}) as Record<string, ContentSubmissionUnit>;
  
  // Get all content units and check approval status
  const contentUnits = getGroupedContent(contentMap, contentId!);
  const isContentApproved = isGroupApproved(contentUnits);

  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const progressRef = useRef<UploadProgressRef>(null);
  const [contentUrls, setContentUrls] = useState<Record<string, string>>({});
  const [isLoadingContent, setIsLoadingContent] = useState<Record<string, boolean>>({});
  const [channelLinks, setChannelLinks] = useState<ChannelLink[]>([]);
  const [submittingChannel, setSubmittingChannel] = useState<SocialChannel | null>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  // Check if content overflows and needs scroll buttons
  useEffect(() => {
    const checkOverflow = () => {
      const hasOverflow = checkContentOverflow('content-scroll-container');
        setShowScrollButtons(hasOverflow);
    };

    // Check initially and on window resize
    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [contentMap]); // Re-check when content changes

  useEffect(() => {
    const loadContentUrls = async () => {
      // Load URLs for all existing content items
      const allContentItems = Object.entries(contentMap).filter(([_, unit]) => 
        unit.requirementId === contentId && unit.fileUrl
      );

      for (const [contentKey, contentUnit] of allContentItems) {
        if (contentUnit.fileUrl && !contentUrls[contentKey]) {
          try {
            setIsLoadingContent(prev => ({ ...prev, [contentKey]: true }));
            const fileRef = ref(storage, contentUnit.fileUrl);
            const url = await getDownloadURL(fileRef);
            setContentUrls(prev => ({ ...prev, [contentKey]: url }));
          } catch (error) {
            console.error(`Error getting content URL for ${contentKey}:`, error);
            toast({
              title: "Error loading content",
              description: "There was an error loading the content. Please try again.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } finally {
            setIsLoadingContent(prev => ({ ...prev, [contentKey]: false }));
          }
        }
      }
    };

    loadContentUrls();
  }, [contentMap, contentId]);

  // Initialize channel links when page loads or channels change
  useEffect(() => {
    if (contentRequirement?.channels && isContentApproved) {
      const existingLinks = collab?.deliverableLinks || {};
      
      const channelToDeliverableMap: Record<SocialChannel, string> = {
        instagramPosts: 'instagramPosts',
        tiktoks: 'tiktoks',
        instagramReels: 'instagramReels',
        instagramStories: 'instagramStories'
      };
      
      const channelLinks = contentRequirement.channels.map(channel => {
        let submittedLink = '';
        let status: 'approved' | 'pendingVerification' | 'pendingSubmission' | undefined;
        
        const deliverableKey = channelToDeliverableMap[channel];
        const links = deliverableKey ? existingLinks[deliverableKey] : undefined;
        
        // The requirement still lists this channel, but its slot may have been withdrawn. Asking for
        // a link anyway sent the creator into `handleSubmitSingleLink`, which finds no live slot and
        // throws a developer string at them.
        const live = Array.isArray(links)
          ? links.find(l => l.contentId === contentId && !isVoided(l))
          : undefined;
        const withdrawn = Array.isArray(links)
          ? links.some(l => l.contentId === contentId && isVoided(l))
          : false;

        if (live?.userLink) {
          submittedLink = live.userLink;
          status = live.status === 'approved' ? 'approved' : live.status === 'pendingSubmission' ? 'pendingSubmission' : 'pendingVerification';
        }

        return {
          channel,
          link: '',
          submittedLink,
          status: status as 'approved' | 'pendingVerification' | 'pendingSubmission',
          rejectionReason: live?.rejectionReason,
          voided: !live && withdrawn,
        };
      });
      
      setChannelLinks(channelLinks);
    }
  }, [contentRequirement?.channels, isContentApproved, collab?.deliverableLinks, contentId]);

  const onDrop = (acceptedFiles: File[], contentKey: string) => {
    if (acceptedFiles.length > 0) {
      const newFiles = { ...uploadedFiles };
      newFiles[contentKey] = acceptedFiles[0];
      setUploadedFiles(newFiles);
    }
  };

  const handleViewGuidelines = () => {
    setGuidelinesOpen(true);
  };




  const handleConfirm = async () => {
    const requiredCount = contentRequirement ? getContentTypeCount(contentRequirement) : 1;
    
    // Get all content items for this requirement, sorted by key for consistent order
    const contentItems = getGroupedContent(contentMap, contentId!);

    // For new content, we need all files
    if (contentItems.length === 0 && Object.keys(uploadedFiles).length < requiredCount) {
      toast({
        title: "More files required.",
        description: `Please upload ${requiredCount} ${contentRequirement?.type || 'content'} file(s). You have uploaded ${Object.keys(uploadedFiles).length}.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // For existing content, we just need the changed files
    if (contentItems.length > 0 && Object.keys(uploadedFiles).length === 0) {
      toast({
        title: "No changes detected.",
        description: "Please upload new content files to replace existing content.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Note: Post links are submitted separately after content is approved

    if (!collabId) {
      toast({
        title: "Error",
        description: "Missing collab ID",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    progressRef.current?.updateProgress(0);

    try {
      // Check if any of the content being submitted is new (status is pendingSubmission)
      const contentKeys = Object.keys(uploadedFiles);
      const hasNewContentToSubmit = hasNewContent(collab?.content, contentKeys);

      // Upload and submit each file with its content key
      for (const [contentKey, file] of Object.entries(uploadedFiles)) {
        const { url, filePath } = await deliverableApi.getUGCUploadUrl({
          fileName: file.name,
          contentType: file.type,
          collabId: collab.id,
          contentId: contentKey,
          clientId: client.id
        }, influencerData?.id);

        await uploadWithProgress(url, file, (progress) => {
          progressRef.current?.updateProgress(progress);
        });
        
        await deliverableApi.submitUGCContent({
          collabId: collab.id,
          postUrl: filePath,
          contentId: contentKey
        }, influencerData?.id);
      }

      // Send notification to client only if there's new content being submitted
      if (hasNewContentToSubmit) {
        deliverableApi.notifyContentReview({
          collabId: collab.id
        }, influencerData?.id);
      }
      
      toast({
        title: "Content submitted successfully!",
        description: `Your ${Object.keys(uploadedFiles).length} content file(s) have been submitted for review.${contentRequirement?.channels?.length ? ' You can submit post links after your content is approved.' : ''}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      setUploadedFiles({});
      navigate(ugcSubmissionPath(collabId!));
      
    } catch (error: any) {
      console.error("ERROR SUBMITTING CONTENT: ", error);
      let errorMessage = "There was an error submitting your content.";
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.status === 413) {
        errorMessage = "File is too large. Please try a smaller file.";
      } else if (error?.name === "AbortError") {
        errorMessage = "Upload was cancelled. Please try again.";
      } else if (error?.message === "Load failed") {
        errorMessage = "Upload failed. Please check your connection and try again.";
      }

      toast({
        title: "Submission failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      progressRef.current?.updateProgress(0);
    }
  };

  const handleChannelLinkChange = (channel: string, value: string) => {
    setChannelLinks(prev => 
      prev.map(cl => 
        cl.channel === channel ? { ...cl, link: value } : cl
      )
    );
  };

  const handleSubmitSingleLink = async (channel: SocialChannel, link: string) => {
    if (!link.trim()) {
      toast({
        title: "Link is required",
        description: "Please enter a valid link before submitting.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const influencerHandle = getInfluencerPrimaryHandle(influencerData);
    if (!collab || !client || !influencerHandle) {
      toast({
        title: "Error",
        description: "Missing required data",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmittingChannel(channel);

    try {
      const channelToDeliverableMap: Record<SocialChannel, string> = {
        instagramPosts: 'instagramPosts',
        tiktoks: 'tiktoks',
        instagramReels: 'instagramReels',
        instagramStories: 'instagramStories'
      };

      const deliverableLinks = collab.deliverableLinks || {};
      const targetKey = channelToDeliverableMap[channel];
      const channelLinks = Array.isArray(deliverableLinks[targetKey]) ? deliverableLinks[targetKey] : [];
      const linkIndex = channelLinks.findIndex(l => l.contentId === contentId && !isVoided(l));

      if (!targetKey || linkIndex < 0) {
        throw new Error(`No available deliverable slot found for ${getChannelDisplayName(channel)}`);
      }

      const deliverableKey = targetKey;

      await deliverableApi.submitPost({
        collabId: collab.id,
        deliverableKey,
        linkIndex,
        postUrl: link.trim(),
        influencerHandle,
        clientName,
        clientId: client.id
      }, influencerData?.id);

      toast({
        title: "Link submitted successfully!",
        description: `Your ${getChannelDisplayName(channel)} link has been submitted.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setChannelLinks(prev => 
        prev.map(cl => 
          cl.channel === channel 
            ? { 
                ...cl, 
                link: '', 
                submittedLink: link.trim(),
                status: 'pendingVerification'
              } 
            : cl
        )
      );

    } catch (error: any) {
      console.error("Error submitting link:", error);
      toast({
        title: "Error submitting link",
        description: error.message || "There was an error submitting your link. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmittingChannel(null);
    }
  };

  const handleBack = () => {
    navigate(ugcSubmissionPath(collabId!));
  };

  const handleFileUpload = (contentKey: string) => {
    triggerFileSelection(contentRequirement, (file) => {
      const newFiles = { ...uploadedFiles };
      newFiles[contentKey] = file;
      setUploadedFiles(newFiles);
    });
  };

  const handleFileRemove = (contentKey: string) => {
    const newFiles = { ...uploadedFiles };
    delete newFiles[contentKey];
    setUploadedFiles(newFiles);
  };



  const allSlotsCovered = areAllSlotsCovered(contentRequirement, contentMap, uploadedFiles, contentId!);

  return (
    <Box minH="100vh" bg="white" fontFamily="display" color={ff.ink}>
      <Box maxW={SUBMISSION_MAX_W} mx="auto" pb="32px">
        <SubmissionHeader
          eyebrow={[clientName, campaign?.name].filter(Boolean).join(' · ') || 'Collab'}
          title={`${contentRequirement ? getContentTypeDisplayName(contentRequirement.duration) : 'Content'} upload`}
          onBack={handleBack}
          client={client}
        />

        <VStack px="20px" pt="18px" spacing="18px" align="stretch">
          {/* Content Requirements Info */}
          {contentRequirement && (
            <ContentRequirements
              contentRequirement={contentRequirement}
              onViewGuidelines={handleViewGuidelines}
            />
          )}

          {/* Current Content Display */}
          {isContentApproved && contentUnits.length > 0 && (
            <Box w="100%">
              <Text fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2} mb="10px">
                Approved content
              </Text>
              <Box position="relative">
                {/* Navigation Buttons */}
                {showScrollButtons && (
                  <>
                    <IconButton
                      aria-label="Previous"
                      icon={<FiChevronLeft size={20} strokeWidth={2.4} />}
                      position="absolute"
                      left="-16px"
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={2}
                      w="44px"
                      h="44px"
                      minW="44px"
                      borderRadius="full"
                      bg="white"
                      color={ff.ink}
                      border="1px solid"
                      borderColor={ff.border}
                      _hover={{ bg: ff.grey }}
                      onClick={() => scrollContentContainer('content-scroll-container', 'left')}
                      display={['none', 'flex']} // Hide on mobile
                    />
                    <IconButton
                      aria-label="Next"
                      icon={<FiChevronRight size={20} strokeWidth={2.4} />}
                      position="absolute"
                      right="-16px"
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={2}
                      w="44px"
                      h="44px"
                      minW="44px"
                      borderRadius="full"
                      bg="white"
                      color={ff.ink}
                      border="1px solid"
                      borderColor={ff.border}
                      _hover={{ bg: ff.grey }}
                      onClick={() => scrollContentContainer('content-scroll-container', 'right')}
                      display={['none', 'flex']} // Hide on mobile
                    />
                  </>
                )}

                {/* Scrollable Content Container */}
                <Box
                  id="content-scroll-container"
                  overflowX="auto"
                  css={{
                    '&::-webkit-scrollbar': {
                      height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: ff.line,
                      borderRadius: '999px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: ff.muted,
                      borderRadius: '999px',
                      '&:hover': {
                        background: ff.text2,
                      },
                    },
                  }}
                >
                  <HStack spacing="12px" py="4px" minW="100%" align="stretch">
                    {Object.entries(contentMap)
                      .filter(([_, unit]) => unit.requirementId === contentId)
                      .map(([key, content], index) => (
                        <Box
                          key={key}
                          minW={["100%", "320px"]}
                          maxW={["100%", "320px"]}
                          flexShrink={0}
                        >
                          <ContentPreview
                            contentKey={key}
                            index={index}
                            contentType={content?.fileUrl?.match(/\.(mp4|mov|avi|wmv)$/i) ? 'video' : 'image'}
                            previewUrl={contentUrls[key]}
                            status={content?.status}
                            isLoadingContent={isLoadingContent[key]}
                            onRemove={handleFileRemove}
                            onReplace={handleFileUpload}
                            isCarousel={isCarouselRequirement(contentRequirement)}
                          />
                        </Box>
                      ))}
                  </HStack>
                </Box>
              </Box>
            </Box>
          )}

          {/* File Upload Area */}
          {!isContentApproved && (
            <ContentUploadSection
              contentRequirement={contentRequirement}
              contentMap={contentMap}
              contentId={contentId!}
              uploadedFiles={uploadedFiles}
              contentUrls={contentUrls}
              isLoadingContent={isLoadingContent}
              onFileUpload={handleFileUpload}
              onFileRemove={handleFileRemove}
              onDrop={onDrop}
            />
          )}

          {/* Add Link Submission Section - Only show after all content is approved */}
          {isContentApproved && channelLinks.length > 0 && (
            <>
              <Box h="1px" bg={ff.line} />
              <ContentLinkSubmission
                channelLinks={channelLinks}
                submittingChannel={submittingChannel}
                onChannelLinkChange={handleChannelLinkChange}
                onSubmitLink={handleSubmitSingleLink}
              />
            </>
          )}

          {/* Upload Progress */}
          <UploadProgress
            ref={progressRef}
            isSubmitting={isSubmitting}
            viewOnly={viewOnly}
            onProgress={(progress) => {
              // Optional: use progress value if needed, but this won't cause re-renders
            }}
          />

          {/* Submit Button */}
          {!isContentApproved && (
            <Box pt="6px">
              <Button
                variant="ink"
                w="100%"
                onClick={handleConfirm}
                isDisabled={!allSlotsCovered || isSubmitting}
                isLoading={isSubmitting}
                leftIcon={<FiUpload size={18} strokeWidth={2.4} />}
                fontSize="16px"
              >
                {Object.keys(uploadedFiles).length > 0 ? 'Submit new content' : 'Submit content'}
              </Button>
              {!allSlotsCovered && (
                <Text fontSize="13px" color={ff.text2} mt="8px" textAlign="center">
                  Make sure every required item is uploaded or approved.
                </Text>
              )}
            </Box>
          )}
        </VStack>
      </Box>

      <ViewGuidelinesSheet
        isOpen={guidelinesOpen}
        onClose={() => setGuidelinesOpen(false)}
        requirement={contentRequirement ?? null}
        clientId={campaign?.clientID}
        clientName={clientName}
      />
    </Box>
  );
};

export default ContentUploadPage;
